
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import { fragmentShaderString } from '@/shaders/fragment'
import References from '@/singletons/references'
import bodySettings from '@/settings/body-settings.json'
import tackSettings from '@/settings/tack-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselBody',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)

    const texDefaultN = computed(() => store.getters.texDefaultN)
    const texBody = computed(() => store.getters.texBody)
    const texBodyN = computed(() => store.getters.texBodyN)

    const configBodyId = computed(() => store.getters.configBodyId)
    const colorpickerBody = computed(() => store.getters.colorpickerBody)
    const isRenderer = inject('isRenderer') as boolean

    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      try {
        initTextureLoadService()
        let mat = materialCreateService.createMaterial(fragmentShaderString, true, true, isRenderer)
        mat.uniforms.diffuse.value = materialCreateService.createColor('#ff0000')
        mat.uniforms.bodyTexture = { value: null }
        mat.uniforms.itoTexture = { value: null }
        mat.uniforms.itoColor = {
          value: materialCreateService.createColor(0xffffff)
        }
        mat.uniforms.futaNoUraOnly = { value: 0 }
        mat.uniforms.futaNoUraWhiteTexture = { value: null }
        mat.uniforms.futaNoUraColor = {
          value: materialCreateService.createColor(0xffffff)
        }
        mat.uniforms.beltTexture = { value: null }
        mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
        mat.uniforms.roughness.value = materialCreateService.getRoughness()
        mat.uniforms.metalness.value = materialCreateService.getMetalness()
        References.instance.setBodyMaterial(mat)
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => isBagLoaded.value, (bag) => { References.instance.getBagReferences().body.material = References.instance.getBodyMaterial() })
    // prettier-ignore
    watch(() => texDefaultN.value, (_) => { References.instance.getBodyMaterial().uniforms.normalMap.value = References.instance.getBagTextures().texDefaultN })
    // prettier-ignore
    watch(() => texBody.value, (_) => { References.instance.getBodyMaterial().uniforms.bodyTexture.value = References.instance.getBagTextures().texBody })
    // prettier-ignore
    watch(() => texBodyN.value, (_) => { References.instance.getBodyMaterial().uniforms.normalMap.value = References.instance.getBagTextures().texBodyN })
    // prettier-ignore
    watch(() => configBodyId.value, (_) => { updateOption() })
    // prettier-ignore
    watch(() => colorpickerBody.value, (_) => { updateColorpicker() })

    function updateOption() {
      try {
        initTextureLoadService()
        if (configBodyId.value) {
          setBodyColor(bodySettings.fullorderColors.find((x: any) => x.id == configBodyId.value))
        } else {
          setBodyColor(bodySettings.fullorderColors.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setBodyColor(obj: any) {
      let color = obj ? materialCreateService.createColor(obj.value) : null
      const zipperColor = obj ? determineZipperColor(obj.id, color) : color
      const covertackColor = obj ? determineCovertackColor(obj.id, color) : color

      References.instance.getBodyMaterial().uniforms.diffuse.value = color
      References.instance.getEdgeMaterial().uniforms.diffuse.value = color
      References.instance.getSideMaterial().uniforms.diffuse.value = color
      References.instance.getPocketMaterial().uniforms.diffuse.value = color
      References.instance.getFrontMaterial().uniforms.diffuse.value = color
      References.instance.getBackMaterial().uniforms.diffuse.value = color
      References.instance.getRibbonMaterial().uniforms.diffuse.value = color
      References.instance.getPocketMaterial().uniforms.zipperColor.value = zipperColor
      References.instance.getCovertackColorMaterial().uniforms.diffuse.value = covertackColor
    }

    function determineZipperColor(id: string, bodyColor: THREE.Color | null) {
      let zipperException = bodySettings.zipperColorExceptions.find((obj) => obj.exceptColorId == id)
      if (zipperException) {
        return materialCreateService.createColor(bodySettings.fullorderColors.find((obj) => obj.id == zipperException!.replaceColorId)!.value)
      }
      return bodyColor
    }

    function determineCovertackColor(bodyId: any, bodyColor: THREE.Color | null) {
      let covertackException = tackSettings.colorExceptions.find((x) => x.bodyId == bodyId)
      if (covertackException) {
        return materialCreateService.createColor(covertackException.value)
      }
      return bodyColor
    }

    function updateColorpicker() {
      let color = colorpickerBody.value ? materialCreateService.createColor(colorpickerBody.value) : null

      References.instance.getBodyMaterial().uniforms.diffuse.value = color
      References.instance.getEdgeMaterial().uniforms.diffuse.value = color
      References.instance.getSideMaterial().uniforms.diffuse.value = color
      References.instance.getPocketMaterial().uniforms.diffuse.value = color
      References.instance.getFrontMaterial().uniforms.diffuse.value = color
      References.instance.getBackMaterial().uniforms.diffuse.value = color
      References.instance.getRibbonMaterial().uniforms.diffuse.value = color
      References.instance.getPocketMaterial().uniforms.zipperColor.value = color
      References.instance.getCovertackColorMaterial().uniforms.diffuse.value = color
    }

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,
      texBody,
      texDefaultN,
      texBodyN,
      configBodyId,
      colorpickerBody
    }
  }
})
